import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, CircularProgress, Tooltip, Paper, Tabs, Tab } from '@mui/material';
import {
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  PlayArrow as ProcessIcon,
  Done as CompleteIcon,
  Error as FailIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Pending as PendingIcon,
  ContentCopy as CopyIcon,
  AccountBalanceWallet as WalletIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import withdrawalService, {
  WithdrawalStatus,
  WithdrawalRequest,
  WithdrawalFilters,
  WithdrawalStatistics
} from '../services/withdrawalService';
import MyShiftStatusBanner from '../components/common/MyShiftStatusBanner';
import JalaliDatePicker, { toPersianDigits } from '../components/common/JalaliDatePicker';
import moment from 'moment-jalaali';
import './EntityManagement.css';

// Helpers: format money with thousands separator + Persian digits
const formatMoney = (v: string | number): string => {
  const s = String(v || '').replace(/,/g, '').replace(/[^\d]/g, '');
  if (!s) return '';
  return toPersianDigits(s.replace(/\B(?=(\d{3})+(?!\d))/g, '٬'));
};

const parseMoney = (v: string): string => {
  return v.replace(/[٬,]/g, '').replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)));
};

const formatJalaliShort = (iso: string): string => {
  if (!iso) return '';
  try { return toPersianDigits(moment(iso).format('jYYYY/jMM/jDD')); }
  catch { return iso; }
};

type TabValue = 'all' | WithdrawalStatus;

const WithdrawalManagement: React.FC = () => {
  // Data
  const [withdrawals, setWithdrawals] = useState<WithdrawalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [statistics, setStatistics] = useState<WithdrawalStatistics | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filtering
  const [filters, setFilters] = useState<WithdrawalFilters>({});
  const [searchInput, setSearchInput] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');

  // Tab
  const [tabValue, setTabValue] = useState<TabValue>('all');

  // Dialogs
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<WithdrawalRequest | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [startProcessingOpen, setStartProcessingOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  // 🏦 واریز خودکار زرین‌پال vs دستی (همراه‌بانک)
  const [payoutConfig, setPayoutConfig] = useState<{ zarinpalEnabled: boolean; minPayoutToman: number; hours: string; withinHours: boolean; note: string } | null>(null);
  const [payMode, setPayMode] = useState<'manual' | 'zarinpal'>('manual');
  const [payoutBusy, setPayoutBusy] = useState<string | null>(null);
  useEffect(() => { withdrawalService.getPayoutConfig().then((c) => { setPayoutConfig(c); }); }, []);
  const PAYOUT_STATUS_LABELS: Record<string, string> = {
    submitted: 'ثبت‌شده در زرین‌پال (در انتظار واریز پایا)', queued: 'در صف واریز (خارج از ساعت مجاز)',
    bank_account_pending: 'در انتظار تأیید شبا در زرین‌پال', paid: 'واریز شد', failed: 'ناموفق',
  };
  const openCompleteDialog = (w: WithdrawalRequest, mode?: 'manual' | 'zarinpal') => {
    setSelectedWithdrawal(w);
    setPaymentReference(w.paymentReference || '');
    setAdminNotes('');
    // پیش‌فرض همیشه «دستی» — واریز خودکار فقط با انتخاب صریح ادمین (جلوگیری از پرداخت دوبارهٔ برداشتی که با همراه‌بانک واریز شده)
    setPayMode(mode || 'manual');
    setCompleteOpen(true);
  };
  const handleSyncPayout = async (w: WithdrawalRequest) => {
    setPayoutBusy(w._id);
    try {
      const r = await withdrawalService.syncPayout(w._id);
      setError('');
      alert(r.message || 'استعلام انجام شد');
      loadWithdrawals();
      loadStatistics();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'خطا در استعلام واریز');
    } finally {
      setPayoutBusy(null);
    }
  };
  const [markFailedOpen, setMarkFailedOpen] = useState(false);

  // Form
  const [adminNotes, setAdminNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [failureReason, setFailureReason] = useState('');
  const [paymentReference, setPaymentReference] = useState('');

  // Error/loading
  const [error, setError] = useState('');
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const loadWithdrawals = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const currentFilters: WithdrawalFilters = { ...filters };
      if (tabValue !== 'all') {
        currentFilters.status = tabValue as WithdrawalStatus;
      }
      const response = await withdrawalService.getWithdrawalRequests(
        page + 1,
        rowsPerPage,
        currentFilters
      );
      setWithdrawals(response.items);
      setTotalCount(response.meta.totalItems);
      // ⚠️ آمار همراه لیست ({count, amount}) شکل دیگری دارد و بدون فیلتر تاریخ است — کارت‌ها فقط از /stats می‌آیند
    } catch (err) {
      console.error('Error loading withdrawals:', err);
      setError('خطا در بارگذاری درخواست‌های برداشت');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters, tabValue]);

  const loadStatisticsFor = useCallback(async (df: string, dt: string) => {
    try {
      const stats = await withdrawalService.getWithdrawalStatistics(df, dt);
      setStatistics(stats);
    } catch (err) {
      console.error('Error loading statistics:', err);
    }
  }, []);
  const loadStatistics = useCallback(async () => loadStatisticsFor(filters.dateFrom || '', filters.dateTo || ''), [loadStatisticsFor, filters.dateFrom, filters.dateTo]);

  useEffect(() => {
    loadWithdrawals();
  }, [loadWithdrawals]);

  useEffect(() => {
    loadStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (_: React.SyntheticEvent, newValue: TabValue) => {
    setTabValue(newValue);
    setPage(0);
  };

  /** اعمال فیلترها با مقادیر صریح (چیپ‌ها مقدار جدید را می‌دهند تا فیلترِ کهنه اعمال نشود) + آمار همان بازه */
  const applyFilters = (o: { search?: string; minAmount?: string; maxAmount?: string; dateFrom?: string; dateTo?: string } = {}) => {
    const s = o.search ?? searchInput, mn = o.minAmount ?? minAmount, mx = o.maxAmount ?? maxAmount, df = o.dateFrom ?? dateFrom, dt = o.dateTo ?? dateTo;
    const newFilters: WithdrawalFilters = {};
    if (mn) newFilters.minAmount = parseFloat(mn);
    if (mx) newFilters.maxAmount = parseFloat(mx);
    if (df) newFilters.dateFrom = df;
    if (dt) newFilters.dateTo = dt;
    if (s.trim()) newFilters.search = s.trim();
    setFilters(newFilters);
    setPage(0);
    loadStatisticsFor(df || '', dt || '');
  };
  const handleSearch = () => applyFilters();

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const clearFilters = () => {
    setSearchInput('');
    setDateFrom('');
    setDateTo('');
    setMinAmount('');
    setMaxAmount('');
    setFilters({});
    setPage(0);
    loadStatisticsFor('', '');
  };

  /** خروجی CSV حسابداری (ناخالص/کارمزد/خالص/تفکیک/مرجع/روش واریز) با فیلتر وضعیت و تاریخِ فعال */
  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      const rows: any[] = await withdrawalService.exportWithdrawals({ ...(tabValue !== 'all' ? { status: tabValue as WithdrawalStatus } : {}), dateFrom: filters.dateFrom, dateTo: filters.dateTo });
      if (!rows.length) { setError('ردیفی برای خروجی پیدا نشد'); return; }
      const esc = (v: any) => { const s = v == null ? '' : String(v); return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
      const jd = (v: any) => (v ? moment(v).format('jYYYY/jMM/jDD HH:mm') : '');
      const header = ['شناسه پیگیری', 'فروشگاه', 'نام فروشنده', 'موبایل', 'ایمیل', 'مبلغ ناخالص', 'کارمزد', 'خالص واریزی', 'بخش فروش', 'بخش بازرسی', 'نرخ کارمزد', 'منبع نرخ', 'سهم معرف', 'وضعیت', 'روش واریز', 'شناسه واریز زرین‌پال', 'وضعیت واریز', 'مرجع پرداخت', 'بانک', 'شبا', 'صاحب حساب', 'تاریخ درخواست', 'تاریخ تأیید', 'تاریخ تکمیل', 'پردازشگر', 'دلیل رد', 'دلیل شکست', 'یادداشت'];
      const lines = rows.map((r) => [r.referenceId, r.storeName, r.sellerName, r.sellerPhone, r.sellerEmail, r.amount, r.processingFee, r.netAmount, r.salePortion, r.inspectionPortion, r.feePercentage, r.feeSource, r.referralPaid, withdrawalService.getStatusText(r.status), r.payoutMode === 'zarinpal' ? 'زرین‌پال' : r.status === 'completed' ? 'دستی' : '', r.payoutId, r.payoutStatus, r.paymentReference, r.bankName, r.iban, r.accountHolder, jd(r.requestedAt), jd(r.approvedAt), jd(r.processedAt), r.processedByName, r.rejectionReason, r.failureReason, r.adminNotes].map(esc).join(','));
      const csv = '\ufeff' + header.map(esc).join(',') + '\r\n' + lines.join('\r\n');
      const url = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a'); a.href = url; a.download = `seller-withdrawals-${moment().format('jYYYY-jMM-jDD')}.csv`; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e?.message || 'خطا در خروجی');
    } finally {
      setExporting(false);
    }
  };

  const handleRefresh = async () => {
    await loadWithdrawals();
    await loadStatistics();
  };

  const handleViewDetails = async (withdrawal: WithdrawalRequest) => {
    setDetailsOpen(true);
    setDetailsLoading(true);
    try {
      const fresh = await withdrawalService.getWithdrawalRequest(withdrawal._id);
      setSelectedWithdrawal(fresh || withdrawal);
    } catch (e) {
      setSelectedWithdrawal(withdrawal);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedWithdrawal) return;
    setActionLoading(true);
    setError('');
    try {
      await withdrawalService.approveWithdrawal(selectedWithdrawal._id, adminNotes);
      setApproveOpen(false);
      setAdminNotes('');
      loadWithdrawals();
      loadStatistics();
    } catch (error: any) {
      setError(error.response?.data?.message || 'خطا در تایید درخواست');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStartProcessing = async () => {
    if (!selectedWithdrawal) return;
    setActionLoading(true);
    setError('');
    try {
      await withdrawalService.startProcessingWithdrawal(
        selectedWithdrawal._id,
        paymentReference,
        adminNotes
      );
      setStartProcessingOpen(false);
      setPaymentReference('');
      setAdminNotes('');
      loadWithdrawals();
      loadStatistics();
    } catch (error: any) {
      setError(error.response?.data?.message || 'خطا در آغاز پردازش');
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!selectedWithdrawal) return;
    setActionLoading(true);
    setError('');
    try {
      if (payMode === 'zarinpal') {
        const r = await withdrawalService.payViaZarinpal(selectedWithdrawal._id, adminNotes);
        alert(r.message);
      } else {
        await withdrawalService.completeWithdrawal(
          selectedWithdrawal._id,
          adminNotes,
          paymentReference.trim() || undefined
        );
      }
      setCompleteOpen(false);
      setAdminNotes('');
      setPaymentReference('');
      loadWithdrawals();
      loadStatistics();
    } catch (error: any) {
      setError(error.response?.data?.message || 'خطا در تکمیل برداشت');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkFailed = async () => {
    if (!selectedWithdrawal || !failureReason) return;
    setActionLoading(true);
    setError('');
    try {
      await withdrawalService.markWithdrawalFailed(
        selectedWithdrawal._id,
        failureReason,
        adminNotes
      );
      setMarkFailedOpen(false);
      setFailureReason('');
      setAdminNotes('');
      loadWithdrawals();
      loadStatistics();
    } catch (error: any) {
      setError(error.response?.data?.message || 'خطا در علامت‌گذاری برداشت');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedWithdrawal || !rejectionReason) return;
    setActionLoading(true);
    setError('');
    try {
      await withdrawalService.rejectWithdrawal(
        selectedWithdrawal._id,
        rejectionReason,
        adminNotes
      );
      setRejectOpen(false);
      setRejectionReason('');
      setAdminNotes('');
      loadWithdrawals();
      loadStatistics();
    } catch (error: any) {
      setError(error.response?.data?.message || 'خطا در رد درخواست');
    } finally {
      setActionLoading(false);
    }
  };

  const formatPrice = (amount: number | undefined): string =>
    (amount || 0).toLocaleString('fa-IR');

  const formatDate = (dateString: string): string =>
    new Date(dateString).toLocaleString('fa-IR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });

  // تشخیص تأیید خودکار از روی تاریخچه‌ی وضعیت (reason ثبت‌شده هنگام auto-approve)
  const isAutoApproved = (w: WithdrawalRequest | null): boolean =>
    !!w?.statusHistory?.some(
      (h) => (h.reason || '').includes('تأیید خودکار') || (h.reason || '').includes('تایید خودکار')
    );

  const copyText = (value?: string) => {
    if (!value) return;
    try { navigator.clipboard.writeText(value); } catch { /* ignore */ }
  };

  const getStatusTag = (status: WithdrawalStatus) => {
    const label = withdrawalService.getStatusText(status);
    let cls = 'tag-warning';
    switch (status) {
      case WithdrawalStatus.COMPLETED:
      case WithdrawalStatus.APPROVED:
        cls = 'tag-success';
        break;
      case WithdrawalStatus.REJECTED:
      case WithdrawalStatus.FAILED:
        cls = 'tag-danger';
        break;
      case WithdrawalStatus.PROCESSING:
        cls = 'tag-info';
        break;
      case WithdrawalStatus.PENDING:
      default:
        cls = 'tag-warning';
        break;
    }
    return <span className={`tag tag-sm ${cls}`}>{label}</span>;
  };

  const totalPages = Math.ceil(totalCount / rowsPerPage) || 1;
  const startItem = totalCount === 0 ? 0 : page * rowsPerPage + 1;
  const endItem = Math.min((page + 1) * rowsPerPage, totalCount);
  const hasActiveFilters = !!(searchInput || dateFrom || dateTo || minAmount || maxAmount);

  const totalStatsCount =
    (statistics?.pending?.count || 0) +
    (statistics?.approved?.count || 0) +
    (statistics?.processing?.count || 0) +
    (statistics?.completed?.count || 0) +
    (statistics?.rejected?.count || 0) +
    (statistics?.failed?.count || 0);

  return (
    <div className="entity-page">
      <MyShiftStatusBanner category="seller_withdrawal" />
      {/* Header */}
      <div className="entity-header">
        <div>
          <h1 className="entity-title">مدیریت درخواست‌های برداشت</h1>
          <p className="entity-subtitle">تأیید و پرداخت درخواست‌های فروشندگان</p>
        </div>
        <div className="header-actions">
          <button className="header-btn" onClick={handleExport} disabled={exporting || loading} title="خروجی CSV حسابداری (فیلتر وضعیت/تاریخ فعال)">
            {exporting ? <CircularProgress size={14} /> : 'خروجی CSV'}
          </button>
          <button
            className="header-btn refresh"
            onClick={handleRefresh}
            disabled={loading}
            title="بروزرسانی"
          >
            <RefreshIcon fontSize="small" />
          </button>
        </div>
      </div>

      {error && (
        <div
          style={{
            margin: '0 0 12px',
            padding: '12px 16px',
            background: '#fee2e2',
            color: '#991b1b',
            borderRadius: 8,
            fontSize: '0.875rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <span>{error}</span>
          <button
            onClick={() => setError('')}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#991b1b' }}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>
      )}

      {/* Stats */}
      {statistics && (
        <div className="entity-stats">
          <div className="entity-stat-card">
            <div className="entity-stat-icon blue"><WalletIcon /></div>
            <div>
              <p className="entity-stat-label">کل درخواست‌ها</p>
              <p className="entity-stat-value">{totalStatsCount.toLocaleString('fa-IR')}</p>
            </div>
          </div>
          <div className="entity-stat-card">
            <div className="entity-stat-icon orange"><PendingIcon /></div>
            <div>
              <p className="entity-stat-label">در انتظار اقدام</p>
              <p className="entity-stat-value">
                {((statistics.pending?.count || 0) + (statistics.approved?.count || 0) + (statistics.processing?.count || 0)).toLocaleString('fa-IR')}
              </p>
              <p style={{ fontSize: 11, color: 'var(--color-text-secondary)', margin: 0 }}>
                {((statistics.pending?.totalAmount || 0) + (statistics.approved?.totalAmount || 0) + (statistics.processing?.totalAmount || 0)).toLocaleString('fa-IR')} تومان
              </p>
            </div>
          </div>
          <div className="entity-stat-card">
            <div className="entity-stat-icon green"><ApproveIcon /></div>
            <div>
              <p className="entity-stat-label">تکمیل شده</p>
              <p className="entity-stat-value">
                {(statistics.completed?.count || 0).toLocaleString('fa-IR')}
              </p>
              <p style={{ fontSize: 11, color: 'var(--color-text-secondary)', margin: 0 }}>
                {(statistics.completed?.totalAmount || 0).toLocaleString('fa-IR')} تومان
              </p>
            </div>
          </div>
          <div className="entity-stat-card">
            <div className="entity-stat-icon blue"><WalletIcon /></div>
            <div>
              <p className="entity-stat-label">کارمزد وصول‌شده</p>
              <p className="entity-stat-value">
                {(statistics.completed?.totalFees || 0).toLocaleString('fa-IR')}
              </p>
              <p style={{ fontSize: 11, color: 'var(--color-text-secondary)', margin: 0 }}>
                تومان (از برداشت‌های تکمیل‌شده)
              </p>
            </div>
          </div>
          <div className="entity-stat-card">
            <div className="entity-stat-icon red"><RejectIcon /></div>
            <div>
              <p className="entity-stat-label">رد/ناموفق</p>
              <p className="entity-stat-value">
                {((statistics.rejected?.count || 0) + (statistics.failed?.count || 0)).toLocaleString('fa-IR')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 2, borderRadius: '12px' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{ px: 2, borderBottom: '1px solid var(--color-border)' }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="همه" value="all" />
          <Tab label="در انتظار تایید" value={WithdrawalStatus.PENDING} />
          <Tab label="تایید شده" value={WithdrawalStatus.APPROVED} />
          <Tab label="در حال پردازش" value={WithdrawalStatus.PROCESSING} />
          <Tab label="تکمیل شده" value={WithdrawalStatus.COMPLETED} />
          <Tab label="رد شده" value={WithdrawalStatus.REJECTED} />
          <Tab label="ناموفق" value={WithdrawalStatus.FAILED} />
        </Tabs>
      </Paper>

      {/* Filters */}
      <div className="entity-filters">
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">
              <SearchIcon fontSize="small" />
              جستجو
            </label>
            <div className="filter-search-wrap">
              <input
                className="filter-field"
                type="text"
                placeholder="شناسه، نام، یا ایمیل..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <span className="filter-search-icon">
                <SearchIcon fontSize="small" />
              </span>
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">حداقل مبلغ (تومان)</label>
            <input
              className="filter-field"
              type="text"
              inputMode="numeric"
              value={formatMoney(minAmount)}
              onChange={(e) => setMinAmount(parseMoney(e.target.value))}
              placeholder="مثلا ۵۰٬۰۰۰"
              dir="ltr"
              style={{ textAlign: 'right' }}
            />
          </div>

          <div className="filter-group">
            <label className="filter-label">حداکثر مبلغ (تومان)</label>
            <input
              className="filter-field"
              type="text"
              inputMode="numeric"
              value={formatMoney(maxAmount)}
              onChange={(e) => setMaxAmount(parseMoney(e.target.value))}
              placeholder="مثلا ۱٬۰۰۰٬۰۰۰"
              dir="ltr"
              style={{ textAlign: 'right' }}
            />
          </div>

          <div className="filter-group">
            <label className="filter-label">از تاریخ</label>
            <JalaliDatePicker
              value={dateFrom}
              onChange={setDateFrom}
              placeholder="تاریخ شروع"
              height={40}
              outputDateOnly
            />
          </div>

          <div className="filter-group">
            <label className="filter-label">تا تاریخ</label>
            <JalaliDatePicker
              value={dateTo}
              onChange={setDateTo}
              placeholder="تاریخ پایان"
              height={40}
              outputDateOnly
            />
          </div>

          <div className="filter-group filter-group-actions">
            <button className="filter-btn" onClick={handleSearch}>
              <SearchIcon fontSize="small" />
              اعمال
            </button>
            {hasActiveFilters && (
              <button className="filter-btn outline" onClick={clearFilters}>
                پاک
              </button>
            )}
          </div>
        </div>

        {hasActiveFilters && (
          <div className="filters-active">
            <span className="filters-label">فیلترهای فعال:</span>
            {searchInput && (
              <span className="filter-tag">
                جستجو: {searchInput}
                <button onClick={() => { setSearchInput(''); applyFilters({ search: '' }); }}>×</button>
              </span>
            )}
            {minAmount && (
              <span className="filter-tag">
                حداقل: {formatMoney(minAmount)} تومان
                <button onClick={() => { setMinAmount(''); applyFilters({ minAmount: '' }); }}>×</button>
              </span>
            )}
            {maxAmount && (
              <span className="filter-tag">
                حداکثر: {formatMoney(maxAmount)} تومان
                <button onClick={() => { setMaxAmount(''); applyFilters({ maxAmount: '' }); }}>×</button>
              </span>
            )}
            {dateFrom && (
              <span className="filter-tag">
                از: {formatJalaliShort(dateFrom)}
                <button onClick={() => { setDateFrom(''); applyFilters({ dateFrom: '' }); }}>×</button>
              </span>
            )}
            {dateTo && (
              <span className="filter-tag">
                تا: {formatJalaliShort(dateTo)}
                <button onClick={() => { setDateTo(''); applyFilters({ dateTo: '' }); }}>×</button>
              </span>
            )}
            <button className="filters-clear" onClick={clearFilters}>پاک کردن همه</button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="entity-table-wrapper">
        {loading ? (
          <div className="entity-loading"><CircularProgress size={32} /></div>
        ) : withdrawals.length === 0 ? (
          <div className="entity-empty">
            <div className="entity-empty-icon"><WalletIcon style={{ fontSize: 48 }} /></div>
            <p className="entity-empty-text">درخواست برداشتی یافت نشد</p>
          </div>
        ) : (
          <>
            <table className="entity-table">
              <thead>
                <tr>
                  <th>شناسه</th>
                  <th>فروشنده</th>
                  <th>فروشگاه</th>
                  <th>مبلغ</th>
                  <th>کارمزد</th>
                  <th>منبع</th>
                  <th>وضعیت</th>
                  <th>تاریخ درخواست</th>
                  <th style={{ width: 140 }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {withdrawals.map((w) => (
                  <tr key={w._id}>
                    <td>
                      <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {w._id.slice(-8)}
                      </span>
                    </td>
                    <td>
                      <div className="entity-info-cell">
                        <div className="entity-info">
                          <span className="entity-name">
                            {w.ownerInfo?.name || w.ownerName || w.sellerName || 'نامشخص'}
                          </span>
                          <span className="entity-desc">
                            {w.ownerInfo?.email || (w as any).ownerEmail || w.sellerEmail || ''}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="entity-info">
                        <span className="entity-name">{w.storeInfo?.name || w.storeName || '-'}</span>
                        {typeof w.storeInfo?.commissionCustom === 'number' && (
                          <span className="entity-desc">کمیسیون: {w.storeInfo.commissionCustom}%</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="entity-info">
                        <span className="entity-name">{formatPrice(w.amount)} تومان</span>
                        {w.netAmount != null && (
                          <span className="entity-desc">خالص: {formatPrice(w.netAmount)}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      {typeof w.commissionAmount === 'number' ? (
                        <div className="entity-info">
                          <span className="entity-name">{formatPrice(w.commissionAmount)}</span>
                          {typeof w.commissionPercentage === 'number' && (
                            <span className="entity-desc">{w.commissionPercentage.toFixed(2)}%</span>
                          )}
                        </div>
                      ) : (
                        <span>-</span>
                      )}
                    </td>
                    {/* ✅ NEW (2026-05-04): bucket-aware source column */}
                    <td>
                      {(w.salePortion ?? 0) > 0 || (w.inspectionPortion ?? 0) > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {(w.inspectionPortion ?? 0) > 0 && (
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: '#15803d',
                                background: 'rgba(52,199,89,0.12)',
                                padding: '1px 6px',
                                borderRadius: 8,
                                whiteSpace: 'nowrap',
                              }}
                              title="درآمد بازرسی — معاف از کارمزد"
                            >
                              بازرسی: {formatPrice(w.inspectionPortion!)}
                            </span>
                          )}
                          {(w.salePortion ?? 0) > 0 && (
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: '#005bbb',
                                background: 'rgba(0,122,255,0.12)',
                                padding: '1px 6px',
                                borderRadius: 8,
                                whiteSpace: 'nowrap',
                              }}
                              title="درآمد فروش — مشمول کارمزد"
                            >
                              فروش: {formatPrice(w.salePortion!)}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span style={{ fontSize: 11, color: '#86868b' }}>—</span>
                      )}
                    </td>
                    <td>{getStatusTag(w.status)}</td>
                    <td>
                      <span className="entity-desc">{formatDate(w.requestedAt)}</span>
                    </td>
                    <td>
                      <div className="actions-cell">
                        <Tooltip title="مشاهده جزئیات" arrow>
                          <button className="action-btn edit" onClick={() => handleViewDetails(w)}>
                            <ViewIcon />
                          </button>
                        </Tooltip>
                        {w.status === WithdrawalStatus.PENDING && (
                          <>
                            <Tooltip title="تایید" arrow>
                              <button
                                className="action-btn toggle active"
                                onClick={() => { setSelectedWithdrawal(w); setApproveOpen(true); }}
                              >
                                <ApproveIcon />
                              </button>
                            </Tooltip>
                            <Tooltip title="رد" arrow>
                              <button
                                className="action-btn delete"
                                onClick={() => { setSelectedWithdrawal(w); setRejectOpen(true); }}
                              >
                                <RejectIcon />
                              </button>
                            </Tooltip>
                          </>
                        )}
                        {w.status === WithdrawalStatus.APPROVED && (
                          <Tooltip title="شروع پردازش" arrow>
                            <button
                              className="action-btn edit"
                              onClick={() => { setSelectedWithdrawal(w); setStartProcessingOpen(true); }}
                            >
                              <ProcessIcon />
                            </button>
                          </Tooltip>
                        )}
                        {w.status === WithdrawalStatus.APPROVED && (
                          <Tooltip title="رد (تأیید اشتباه)" arrow>
                            <button
                              className="action-btn delete"
                              onClick={() => { setSelectedWithdrawal(w); setRejectOpen(true); }}
                            >
                              <RejectIcon />
                            </button>
                          </Tooltip>
                        )}
                        {w.status === WithdrawalStatus.APPROVED && payoutConfig?.zarinpalEnabled && (
                          <Tooltip title="واریز خودکار از زرین‌پال" arrow>
                            <button className="action-btn toggle active" onClick={() => openCompleteDialog(w, 'zarinpal')}>
                              <CompleteIcon />
                            </button>
                          </Tooltip>
                        )}
                        {w.status === WithdrawalStatus.PROCESSING && w.payout?.provider === 'zarinpal' && w.payout.status !== 'paid' && (
                          <Tooltip title={`استعلام واریز زرین‌پال — ${PAYOUT_STATUS_LABELS[w.payout.status] || w.payout.status}`} arrow>
                            <button className="action-btn edit" disabled={payoutBusy === w._id} onClick={() => handleSyncPayout(w)}>
                              <ProcessIcon />
                            </button>
                          </Tooltip>
                        )}
                        {w.status === WithdrawalStatus.PROCESSING && (
                          <>
                            <Tooltip title="تکمیل" arrow>
                              <button
                                className="action-btn toggle active"
                                onClick={() => openCompleteDialog(w)}
                                disabled={!!(w.payout?.provider === 'zarinpal' && !['paid', 'failed'].includes(String(w.payout.status)))}
                                title={w.payout?.provider === 'zarinpal' && !['paid', 'failed'].includes(String(w.payout.status)) ? 'واریز خودکار زرین‌پال در جریان است؛ ابتدا استعلام بزنید' : 'تکمیل'}
                              >
                                <CompleteIcon />
                              </button>
                            </Tooltip>
                            <Tooltip title="علامت‌گذاری ناموفق" arrow>
                              <button
                                className="action-btn delete"
                                onClick={() => { setSelectedWithdrawal(w); setMarkFailedOpen(true); }}
                              >
                                <FailIcon />
                              </button>
                            </Tooltip>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="entity-cards">
              {withdrawals.map((w) => (
                <div key={w._id} className="entity-card">
                  <div className="entity-card-header">
                    <div className="entity-card-image"><WalletIcon /></div>
                    <div className="entity-card-info">
                      <div className="entity-card-name">
                        {w.ownerInfo?.name || w.ownerName || w.sellerName || 'نامشخص'}
                      </div>
                      <div className="entity-card-desc">
                        {w.storeInfo?.name || w.storeName || '-'}
                      </div>
                      <div className="entity-card-desc">
                        {formatPrice(w.amount)} تومان
                      </div>
                    </div>
                    <div className="entity-card-status">{getStatusTag(w.status)}</div>
                  </div>
                  <div className="entity-card-actions">
                    <button className="entity-card-btn primary" onClick={() => handleViewDetails(w)}>
                      <ViewIcon fontSize="small" /> جزئیات
                    </button>
                    {w.status === WithdrawalStatus.PENDING && (
                      <>
                        <button
                          className="entity-card-btn"
                          onClick={() => { setSelectedWithdrawal(w); setApproveOpen(true); }}
                        >
                          <ApproveIcon fontSize="small" /> تایید
                        </button>
                        <button
                          className="entity-card-btn"
                          onClick={() => { setSelectedWithdrawal(w); setRejectOpen(true); }}
                        >
                          <RejectIcon fontSize="small" /> رد
                        </button>
                      </>
                    )}
                    {w.status === WithdrawalStatus.APPROVED && (
                      <button
                        className="entity-card-btn"
                        onClick={() => { setSelectedWithdrawal(w); setStartProcessingOpen(true); }}
                      >
                        <ProcessIcon fontSize="small" /> پردازش
                      </button>
                    )}
                    {w.status === WithdrawalStatus.APPROVED && payoutConfig?.zarinpalEnabled && (
                      <button className="entity-card-btn primary" onClick={() => openCompleteDialog(w, 'zarinpal')}>
                        <CompleteIcon fontSize="small" /> واریز زرین‌پال
                      </button>
                    )}
                    {w.status === WithdrawalStatus.PROCESSING && w.payout?.provider === 'zarinpal' && w.payout.status !== 'paid' && (
                      <button className="entity-card-btn" disabled={payoutBusy === w._id} onClick={() => handleSyncPayout(w)}>
                        <ProcessIcon fontSize="small" /> استعلام واریز
                      </button>
                    )}
                    {w.status === WithdrawalStatus.PROCESSING && (
                      <>
                        <button
                          className="entity-card-btn"
                          onClick={() => openCompleteDialog(w)}
                          disabled={!!(w.payout?.provider === 'zarinpal' && !['paid', 'failed'].includes(String(w.payout.status)))}
                        >
                          <CompleteIcon fontSize="small" /> تکمیل
                        </button>
                        <button
                          className="entity-card-btn"
                          onClick={() => { setSelectedWithdrawal(w); setMarkFailedOpen(true); }}
                        >
                          <FailIcon fontSize="small" /> ناموفق
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="entity-pagination">
              <div className="pagination-info">
                نمایش {startItem} تا {endItem} از {totalCount}
              </div>
              <div className="pagination-controls">
                <select
                  className="pagination-select"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <button
                  className="pagination-btn"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages - 1}
                >
                  <ChevronRightIcon fontSize="small" />
                </button>
                <span style={{ fontSize: 12 }}>{page + 1} / {totalPages}</span>
                <button
                  className="pagination-btn"
                  onClick={() => setPage(page - 1)}
                  disabled={page <= 0}
                >
                  <ChevronLeftIcon fontSize="small" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
        className="withdrawal-details-dialog"
      >
        <div className="edit-dialog-title">
          <span>جزئیات درخواست برداشت</span>
          <button className="details-close" onClick={() => setDetailsOpen(false)}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          {detailsLoading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <CircularProgress />
            </div>
          ) : selectedWithdrawal && (
            <div className="edit-form">
              {/* Summary */}
              <div className="edit-card">
                <div className="edit-card-header">خلاصه مالی</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>مبلغ درخواستی</label>
                      <input type="text" value={`${formatPrice(selectedWithdrawal.amount)} تومان`} readOnly />
                    </div>
                    <div className="edit-field">
                      <label>کارمزد</label>
                      <input
                        type="text"
                        value={
                          typeof selectedWithdrawal.commissionAmount === 'number'
                            ? `${formatPrice(selectedWithdrawal.commissionAmount)} تومان` +
                              (typeof selectedWithdrawal.feePercentage === 'number'
                                ? ` (${selectedWithdrawal.feePercentage}% روی بخش فروش)`
                                : typeof selectedWithdrawal.commissionPercentage === 'number'
                                  ? ` (${selectedWithdrawal.commissionPercentage.toFixed(2)}%)`
                                  : '')
                            : '-'
                        }
                        readOnly
                      />
                    </div>
                    <div className="edit-field full">
                      <label>مبلغ خالص واریزی</label>
                      <input
                        type="text"
                        value={
                          typeof selectedWithdrawal.netAmount === 'number'
                            ? `${formatPrice(selectedWithdrawal.netAmount)} تومان`
                            : '-'
                        }
                        readOnly
                      />
                    </div>
                  </div>

                  {/* ✅ NEW (2026-05-04): bucket-aware source breakdown */}
                  {((selectedWithdrawal.salePortion ?? 0) > 0 ||
                    (selectedWithdrawal.inspectionPortion ?? 0) > 0) && (
                    <div
                      style={{
                        marginTop: 12,
                        padding: 10,
                        borderRadius: 6,
                        border: '1px dashed #d1d1d6',
                        background: '#fafafa',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 6,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: '#86868b',
                          letterSpacing: 0.4,
                        }}
                      >
                        تفکیک منبع برداشت
                      </div>

                      {(selectedWithdrawal.inspectionPortion ?? 0) > 0 && (
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '6px 8px',
                            borderRadius: 4,
                            background: 'rgba(52,199,89,0.06)',
                            border: '1px solid rgba(52,199,89,0.18)',
                          }}
                        >
                          <span style={{ fontSize: 12, color: '#1d1d1f', fontWeight: 600 }}>
                            🟩 از درآمد بازرسی{' '}
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: '#15803d',
                                background: 'rgba(52,199,89,0.14)',
                                padding: '1px 6px',
                                borderRadius: 8,
                                marginRight: 4,
                              }}
                            >
                              معاف از کارمزد
                            </span>
                          </span>
                          <span
                            style={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: '#15803d',
                              fontFamily: 'monospace',
                            }}
                          >
                            {formatPrice(selectedWithdrawal.inspectionPortion!)} تومان
                          </span>
                        </div>
                      )}

                      {(selectedWithdrawal.salePortion ?? 0) > 0 && (
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '6px 8px',
                            borderRadius: 4,
                            background: 'rgba(0,122,255,0.06)',
                            border: '1px solid rgba(0,122,255,0.18)',
                          }}
                        >
                          <span style={{ fontSize: 12, color: '#1d1d1f', fontWeight: 600 }}>
                            🟦 از درآمد فروش{' '}
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: '#005bbb',
                                background: 'rgba(0,122,255,0.14)',
                                padding: '1px 6px',
                                borderRadius: 8,
                                marginRight: 4,
                              }}
                            >
                              کارمزد {selectedWithdrawal.feePercentage ?? selectedWithdrawal.commissionPercentage?.toFixed(0) ?? 5}٪
                            </span>
                          </span>
                          <span
                            style={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: '#005bbb',
                              fontFamily: 'monospace',
                            }}
                          >
                            {formatPrice(selectedWithdrawal.salePortion!)} تومان
                          </span>
                        </div>
                      )}

                      {/* ✅ NEW (2026-07-09): تفکیک پله‌های کمیسیون (پلکان حاشیه‌ای)
                          ✅ UPDATE (2026-08-16): ردیف‌های مبلغ‌ثابت هم نمایش داده می‌شوند */}
                      {selectedWithdrawal.feeBreakdown &&
                        (selectedWithdrawal.feeBreakdown.length > 1 ||
                          (selectedWithdrawal.feeBreakdown.length === 1 &&
                            selectedWithdrawal.feeBreakdown[0].kind === 'fixed')) && (
                        <div
                          style={{
                            marginTop: 4,
                            padding: '6px 8px',
                            borderRadius: 4,
                            background: 'rgba(175,82,222,0.05)',
                            border: '1px solid rgba(175,82,222,0.18)',
                          }}
                        >
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', marginBottom: 4 }}>
                            تفکیک پله‌های کارمزد
                            {selectedWithdrawal.feeScheme?.source === 'store-tiers' && ' (پلکان اختصاصی فروشگاه)'}
                            {selectedWithdrawal.feeScheme?.source === 'global-tiers' && ' (پلکان سراسری)'}
                            {selectedWithdrawal.feeScheme?.source === 'store-fixed' && ' (مبلغ‌ثابت اختصاصی فروشگاه)'}
                            {selectedWithdrawal.feeScheme?.source === 'store-fixed-tiers' && ' (مبلغ‌ثابت پلکانی اختصاصی)'}
                            {selectedWithdrawal.feeScheme?.source === 'global-fixed' && ' (مبلغ‌ثابت سراسری)'}
                            {selectedWithdrawal.feeScheme?.source === 'global-fixed-tiers' && ' (مبلغ‌ثابت پلکانی سراسری)'}
                            {selectedWithdrawal.feeScheme?.source?.startsWith('plan') &&
                              ` (پلن اشتراک${(selectedWithdrawal.feeScheme as any)?.planName ? ` ${(selectedWithdrawal.feeScheme as any).planName}` : ''})`}
                          </div>
                          {selectedWithdrawal.feeBreakdown.map((row, i) => (
                            <div
                              key={i}
                              style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '2px 0' }}
                            >
                              <span style={{ color: '#1d1d1f' }}>
                                {row.to === null
                                  ? `مازاد بر ${formatPrice(row.from)}`
                                  : `${formatPrice(row.from)} تا ${formatPrice(row.to)}`}{' '}
                                {row.kind === 'fixed'
                                  ? `(مبلغ ثابت ${formatPrice(row.amount ?? row.fee)} تومان)`
                                  : `(${row.percent}٪)`}
                              </span>
                              <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#7c3aed' }}>
                                {formatPrice(row.fee)} تومان
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Request info */}
              <div className="edit-card">
                <div className="edit-card-header">اطلاعات درخواست</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field full">
                      <label>شناسه</label>
                      <input type="text" value={selectedWithdrawal._id} readOnly style={{ fontFamily: 'monospace' }} />
                    </div>
                    <div className="edit-field">
                      <label>وضعیت</label>
                      <div style={{ paddingTop: 4, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        {getStatusTag(selectedWithdrawal.status)}
                        {isAutoApproved(selectedWithdrawal) && (
                          <span className="tag tag-sm tag-info" title="این درخواست به‌دلیل مبلغ پایین، بدون دخالت ادمین تأیید شده">⚡ تأیید خودکار</span>
                        )}
                      </div>
                    </div>
                    <div className="edit-field">
                      <label>تاریخ درخواست</label>
                      <input type="text" value={formatDate(selectedWithdrawal.requestedAt)} readOnly />
                    </div>
                    {selectedWithdrawal.approvedAt && (
                      <div className="edit-field">
                        <label>تاریخ تایید</label>
                        <input type="text" value={formatDate(selectedWithdrawal.approvedAt)} readOnly />
                      </div>
                    )}
                    {selectedWithdrawal.processedAt && (
                      <div className="edit-field">
                        <label>تاریخ پردازش/تسویه</label>
                        <input type="text" value={formatDate(selectedWithdrawal.processedAt)} readOnly />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ✅ NEW (2026-07-09): چک زنده‌ی موجودی فروشنده — ریسک قبل از تأیید/تکمیل */}
              {selectedWithdrawal.balanceCheck && (
                <div className="edit-card">
                  <div className="edit-card-header">وضعیت موجودی فروشنده (لحظه‌ای)</div>
                  <div className="edit-card-body">
                    <div
                      style={{
                        padding: 10,
                        borderRadius: 8,
                        marginBottom: 10,
                        fontSize: 13,
                        fontWeight: 600,
                        background: selectedWithdrawal.balanceCheck.sufficient ? 'rgba(52,199,89,0.08)' : 'rgba(255,59,48,0.08)',
                        border: `1px solid ${selectedWithdrawal.balanceCheck.sufficient ? 'rgba(52,199,89,0.3)' : 'rgba(255,59,48,0.35)'}`,
                        color: selectedWithdrawal.balanceCheck.sufficient ? '#15803d' : '#b91c1c'
                      }}
                    >
                      {selectedWithdrawal.balanceCheck.sufficient
                        ? '✅ رزرو این درخواست سالم است — موجودی فروشنده پس از کسر این درخواست منفی نشده و پرداخت بدون ریسک است.'
                        : `⚠️ هشدار: موجودی فروشنده بعد از ثبت این درخواست ${formatPrice(selectedWithdrawal.balanceCheck.debt || 0)} تومان کم آورده (ریفاند/جریمه/اختلاف/تعهد استرداد). تکمیل با خطای موجودی متوقف می‌شود — قبل از پرداخت وضعیت مالی فروشنده را بررسی کنید.`}
                    </div>
                    <div className="edit-form-grid">
                      <div className="edit-field">
                        <label>موجودی قابل برداشت فعلی (بعد از رزرو این درخواست)</label>
                        <input type="text" value={`${formatPrice(selectedWithdrawal.balanceCheck.availableBalance)} تومان`} readOnly />
                      </div>
                      {typeof selectedWithdrawal.balanceCheck.availableInspectionBalance === 'number' && (
                        <div className="edit-field">
                          <label>سهم بازرسی (معاف از کارمزد)</label>
                          <input type="text" value={`${formatPrice(selectedWithdrawal.balanceCheck.availableInspectionBalance)} تومان`} readOnly />
                        </div>
                      )}
                      <div className="edit-field full">
                        <label>زمان بررسی</label>
                        <input type="text" value={formatDate(selectedWithdrawal.balanceCheck.checkedAt)} readOnly />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ✅ NEW (2026-07-09): سابقه‌ی برداشت فروشنده — قضاوت سریع ادمین */}
              {selectedWithdrawal.sellerWithdrawalSummary && (
                <div className="edit-card">
                  <div className="edit-card-header">سابقه برداشت‌های این فروشنده</div>
                  <div className="edit-card-body">
                    <div className="edit-form-grid">
                      <div className="edit-field">
                        <label>مجموع برداشت‌شده تاکنون</label>
                        <input type="text" value={`${formatPrice(selectedWithdrawal.sellerWithdrawalSummary.totalWithdrawn)} تومان`} readOnly />
                      </div>
                      <div className="edit-field">
                        <label>برداشت‌های موفق</label>
                        <input type="text" value={formatPrice(selectedWithdrawal.sellerWithdrawalSummary.completedCount)} readOnly />
                      </div>
                      <div className="edit-field">
                        <label>ردشده</label>
                        <input type="text" value={formatPrice(selectedWithdrawal.sellerWithdrawalSummary.rejectedCount)} readOnly />
                      </div>
                      <div className="edit-field">
                        <label>ناموفق</label>
                        <input type="text" value={formatPrice(selectedWithdrawal.sellerWithdrawalSummary.failedCount)} readOnly />
                      </div>
                    </div>
                    {selectedWithdrawal.sellerWithdrawalSummary.completedCount === 0 && (
                      <div style={{ marginTop: 8, padding: 8, borderRadius: 6, background: 'rgba(255,149,0,0.08)', border: '1px solid rgba(255,149,0,0.3)', fontSize: 12, color: '#92400e', fontWeight: 600 }}>
                        🔎 اولین برداشت این فروشنده است — اطلاعات بانکی را با دقت بیشتری تطبیق دهید.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Store */}
              <div className="edit-card">
                <div className="edit-card-header">اطلاعات فروشگاه و مالک</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>نام فروشگاه</label>
                      <input
                        type="text"
                        value={selectedWithdrawal.storeInfo?.name || selectedWithdrawal.storeName || '-'}
                        readOnly
                      />
                    </div>
                    <div className="edit-field">
                      <label>کمیسیون اختصاصی</label>
                      <input
                        type="text"
                        value={
                          typeof selectedWithdrawal.storeInfo?.commissionCustom === 'number'
                            ? `${selectedWithdrawal.storeInfo.commissionCustom}%`
                            : '—'
                        }
                        readOnly
                      />
                    </div>
                    <div className="edit-field">
                      <label>نام مالک</label>
                      <input
                        type="text"
                        value={selectedWithdrawal.ownerInfo?.name || selectedWithdrawal.sellerName || '-'}
                        readOnly
                      />
                    </div>
                    <div className="edit-field">
                      <label>ایمیل</label>
                      <input
                        type="text"
                        value={selectedWithdrawal.ownerInfo?.email || selectedWithdrawal.sellerEmail || '-'}
                        readOnly
                      />
                    </div>
                    <div className="edit-field full">
                      <label>تلفن</label>
                      <input type="text" value={selectedWithdrawal.ownerInfo?.phone || '—'} readOnly />
                    </div>
                  </div>
                </div>
              </div>

              {/* Bank info */}
              {selectedWithdrawal.bankInfo && (
                <div className="edit-card">
                  <div className="edit-card-header">اطلاعات بانکی</div>
                  <div className="edit-card-body">
                    <div className="edit-form-grid">
                      <div className="edit-field">
                        <label>نام بانک</label>
                        <input type="text" value={selectedWithdrawal.bankInfo.bankName} readOnly />
                      </div>
                      <div className="edit-field">
                        <label>نام صاحب حساب</label>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <input type="text" value={selectedWithdrawal.bankInfo.accountHolder} readOnly style={{ flex: 1 }} />
                          <Tooltip title="کپی" arrow>
                            <button
                              type="button"
                              className="action-btn edit"
                              onClick={() => copyText(selectedWithdrawal.bankInfo?.accountHolder)}
                            >
                              <CopyIcon fontSize="small" />
                            </button>
                          </Tooltip>
                        </div>
                      </div>
                      <div className="edit-field full">
                        <label>شبا / شماره حساب</label>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <input
                            type="text"
                            value={
                              selectedWithdrawal.bankInfo.iban ||
                              selectedWithdrawal.bankInfo.accountNumber ||
                              ''
                            }
                            readOnly
                            style={{ fontFamily: 'monospace', flex: 1 }}
                          />
                          <Tooltip title="کپی" arrow>
                            <button
                              type="button"
                              className="action-btn edit"
                              onClick={() =>
                                copyText(selectedWithdrawal.bankInfo?.iban || selectedWithdrawal.bankInfo?.accountNumber)
                              }
                            >
                              <CopyIcon fontSize="small" />
                            </button>
                          </Tooltip>
                        </div>
                      </div>
                      <div className="edit-field full">
                        <label>مبلغ خالص واریزی (برای کپی)</label>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <input
                            type="text"
                            value={String(selectedWithdrawal.netAmount ?? '')}
                            readOnly
                            dir="ltr"
                            style={{ fontFamily: 'monospace', flex: 1 }}
                          />
                          <Tooltip title="کپی مبلغ (رقم خام برای پنل بانک)" arrow>
                            <button
                              type="button"
                              className="action-btn edit"
                              onClick={() => copyText(String(selectedWithdrawal.netAmount ?? ''))}
                            >
                              <CopyIcon fontSize="small" />
                            </button>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Referral info */}
              {selectedWithdrawal.referralInfo && (
                <div className="edit-card">
                  <div className="edit-card-header">اطلاعات ریفرال</div>
                  <div className="edit-card-body">
                    <div className="edit-form-grid">
                      <div className="edit-field">
                        <label>فعال</label>
                        <input type="text" value={selectedWithdrawal.referralInfo.active ? 'بله' : 'خیر'} readOnly />
                      </div>
                      <div className="edit-field">
                        <label>درصد سهم</label>
                        <input
                          type="text"
                          value={`${Number(selectedWithdrawal.referralInfo.percentage || 0).toFixed(2)}%`}
                          readOnly
                        />
                      </div>
                      <div className="edit-field">
                        <label>کارمزد پایه</label>
                        <input
                          type="text"
                          value={`${formatPrice(selectedWithdrawal.referralInfo.baseFee)} تومان`}
                          readOnly
                        />
                      </div>
                      <div className="edit-field">
                        <label>سهم ریفرر{selectedWithdrawal.referralInfo.snapshot ? ' (پرداخت‌شدهٔ واقعی)' : ' (برآورد با نرخ فعلی)'}</label>
                        <input
                          type="text"
                          value={`${formatPrice(selectedWithdrawal.referralInfo.referralAmount)} تومان${selectedWithdrawal.referralInfo.pending ? ' — در انتظار پرداخت' : ''}${selectedWithdrawal.referralInfo.error ? ` — خطا: ${selectedWithdrawal.referralInfo.error}` : ''}`}
                          readOnly
                        />
                      </div>
                      <div className="edit-field full">
                        <label>سهم پلتفرم</label>
                        <input
                          type="text"
                          value={`${formatPrice(selectedWithdrawal.referralInfo.platformAmount)} تومان`}
                          readOnly
                        />
                      </div>
                      {selectedWithdrawal.referralInfo.referrer && (
                        <>
                          <div className="edit-field">
                            <label>نام معرف</label>
                            <input type="text" value={selectedWithdrawal.referralInfo.referrer.name} readOnly />
                          </div>
                          <div className="edit-field">
                            <label>ایمیل معرف</label>
                            <input type="text" value={selectedWithdrawal.referralInfo.referrer.email} readOnly />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* ✅ NEW (2026-07-09): تایم‌لاین تاریخچه وضعیت — چه کسی، کی، چرا */}
              {selectedWithdrawal.statusHistory && selectedWithdrawal.statusHistory.length > 0 && (
                <div className="edit-card">
                  <div className="edit-card-header">تاریخچه وضعیت</div>
                  <div className="edit-card-body">
                    {[...selectedWithdrawal.statusHistory]
                      .sort((a, b) => new Date(a.changedAt).getTime() - new Date(b.changedAt).getTime())
                      .map((h, i, arr) => (
                        <div
                          key={i}
                          style={{
                            display: 'flex',
                            gap: 10,
                            alignItems: 'flex-start',
                            padding: '8px 0',
                            borderBottom: i < arr.length - 1 ? '1px dashed var(--color-border, #e5e5ea)' : 'none'
                          }}
                        >
                          <div style={{ minWidth: 84 }}>{getStatusTag(h.status)}</div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 12.5, color: 'var(--color-text-primary)' }}>{h.reason || '—'}</div>
                            {h.notes && (
                              <div style={{ fontSize: 11.5, color: 'var(--color-text-secondary)', marginTop: 2 }}>{h.notes}</div>
                            )}
                          </div>
                          <div style={{ fontSize: 11.5, color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
                            {formatDate(h.changedAt)}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Reasons/notes */}
              {(selectedWithdrawal.rejectionReason || selectedWithdrawal.failureReason || selectedWithdrawal.notes) && (
                <div className="edit-card">
                  <div className="edit-card-header">توضیحات</div>
                  <div className="edit-card-body">
                    <div className="edit-form-grid">
                      {selectedWithdrawal.rejectionReason && (
                        <div className="edit-field full">
                          <label>دلیل رد</label>
                          <textarea value={selectedWithdrawal.rejectionReason} readOnly rows={2} />
                        </div>
                      )}
                      {selectedWithdrawal.failureReason && (
                        <div className="edit-field full">
                          <label>دلیل ناموفق بودن</label>
                          <textarea value={selectedWithdrawal.failureReason} readOnly rows={2} />
                        </div>
                      )}
                      {selectedWithdrawal.notes && (
                        <div className="edit-field full">
                          <label>یادداشت ادمین</label>
                          <textarea value={selectedWithdrawal.notes} readOnly rows={2} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={() => setDetailsOpen(false)}>بستن</button>
        </div>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog
        open={approveOpen}
        onClose={() => !actionLoading && setApproveOpen(false)}
        maxWidth="sm"
        fullWidth
        className="withdrawal-action-dialog"
      >
        <div className="edit-dialog-title">
          <span>تایید درخواست برداشت</span>
          <button className="details-close" onClick={() => !actionLoading && setApproveOpen(false)}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          <div className="edit-form">
            <div className="edit-card">
              <div className="edit-card-header">تایید</div>
              <div className="edit-card-body">
                <div className="edit-form-grid">
                  <div className="edit-field full">
                    <label>یادداشت ادمین (اختیاری)</label>
                    <textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      placeholder="یادداشت‌های مربوط به تایید..."
                      rows={3}
                      disabled={actionLoading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={() => setApproveOpen(false)} disabled={actionLoading}>
            انصراف
          </button>
          <button className="edit-btn submit" onClick={handleApprove} disabled={actionLoading}>
            {actionLoading && <CircularProgress size={16} style={{ color: '#fff' }} />}
            تایید درخواست
          </button>
        </div>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog
        open={rejectOpen}
        onClose={() => !actionLoading && setRejectOpen(false)}
        maxWidth="sm"
        fullWidth
        className="withdrawal-action-dialog"
      >
        <div className="edit-dialog-title">
          <span>رد درخواست برداشت</span>
          <button className="details-close" onClick={() => !actionLoading && setRejectOpen(false)}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          <div className="edit-form">
            <div className="edit-card">
              <div className="edit-card-header">رد</div>
              <div className="edit-card-body">
                <div className="edit-form-grid">
                  <div className="edit-field full">
                    <label>دلیل رد <span style={{ color: '#dc2626' }}>*</span></label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="مثال: عدم تطابق اطلاعات بانکی، مدارک ناقص، ..."
                      rows={2}
                      disabled={actionLoading}
                    />
                  </div>
                  <div className="edit-field full">
                    <label>یادداشت ادمین (اختیاری)</label>
                    <textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      placeholder="یادداشت‌های اضافی..."
                      rows={3}
                      disabled={actionLoading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={() => setRejectOpen(false)} disabled={actionLoading}>
            انصراف
          </button>
          <button
            className="edit-btn submit"
            onClick={handleReject}
            disabled={!rejectionReason.trim() || actionLoading}
          >
            {actionLoading && <CircularProgress size={16} style={{ color: '#fff' }} />}
            رد درخواست
          </button>
        </div>
      </Dialog>

      {/* Start Processing Dialog */}
      <Dialog
        open={startProcessingOpen}
        onClose={() => !actionLoading && setStartProcessingOpen(false)}
        maxWidth="sm"
        fullWidth
        className="withdrawal-action-dialog"
      >
        <div className="edit-dialog-title">
          <span>شروع پردازش برداشت</span>
          <button className="details-close" onClick={() => !actionLoading && setStartProcessingOpen(false)}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          <div className="edit-form">
            <div className="edit-card">
              <div className="edit-card-header">پردازش</div>
              <div className="edit-card-body">
                <div className="edit-form-grid">
                  <div className="edit-field full">
                    <label>مرجع پردازش (اختیاری)</label>
                    <input
                      type="text"
                      value={paymentReference}
                      onChange={(e) => setPaymentReference(e.target.value)}
                      placeholder="مثال: تراکنش 123456789"
                      disabled={actionLoading}
                    />
                    <div style={{ fontSize: 11.5, color: 'var(--color-text-secondary)', marginTop: 4 }}>
                      معمولاً شماره پیگیری بانکی بعد از واریز مشخص می‌شود — می‌توانید اینجا خالی بگذارید و هنگام «تکمیل» آن را ثبت کنید.
                    </div>
                  </div>
                  <div className="edit-field full">
                    <label>یادداشت ادمین (اختیاری)</label>
                    <textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      placeholder="یادداشت‌های مربوط به پردازش..."
                      rows={3}
                      disabled={actionLoading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
        <div className="edit-dialog-actions">
          <button
            className="edit-btn cancel"
            onClick={() => setStartProcessingOpen(false)}
            disabled={actionLoading}
          >
            انصراف
          </button>
          <button
            className="edit-btn submit"
            onClick={handleStartProcessing}
            disabled={actionLoading}
          >
            {actionLoading && <CircularProgress size={16} style={{ color: '#fff' }} />}
            شروع پردازش
          </button>
        </div>
      </Dialog>

      {/* Complete Dialog */}
      <Dialog
        open={completeOpen}
        onClose={() => !actionLoading && setCompleteOpen(false)}
        maxWidth="sm"
        fullWidth
        className="withdrawal-action-dialog"
      >
        <div className="edit-dialog-title">
          <span>تکمیل برداشت</span>
          <button className="details-close" onClick={() => !actionLoading && setCompleteOpen(false)}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          <div className="edit-form">
            <div className="edit-card">
              <div className="edit-card-header">تکمیل</div>
              <div className="edit-card-body">
                {selectedWithdrawal && (
                  <p
                    style={{
                      marginTop: 0,
                      padding: 12,
                      background: 'rgba(37, 99, 235, 0.08)',
                      borderRadius: 8,
                      fontSize: '0.875rem'
                    }}
                  >
                    مبلغ خالص {formatPrice(selectedWithdrawal.netAmount ?? (selectedWithdrawal.amount - (selectedWithdrawal.commissionAmount || 0)))} تومان (ناخالص {formatPrice(selectedWithdrawal.amount)}) به حساب فروشنده واریز خواهد شد.
                    {selectedWithdrawal.bankInfo?.iban && <> شبا: <span style={{ direction: 'ltr', display: 'inline-block' }}>{selectedWithdrawal.bankInfo.iban}</span></>}
                  </p>
                )}
                <div className="edit-form-grid">
                  <div className="edit-field full">
                    <label>روش واریز</label>
                    <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 6, cursor: payoutConfig?.zarinpalEnabled ? 'pointer' : 'not-allowed', opacity: payoutConfig?.zarinpalEnabled ? 1 : 0.6 }}>
                      <input type="radio" name="pay-mode" checked={payMode === 'zarinpal'} disabled={!payoutConfig?.zarinpalEnabled || actionLoading} onChange={() => setPayMode('zarinpal')} />
                      <span>
                        <strong>واریز خودکار از زرین‌پال</strong> (PayoutAdd به شبای فروشنده)
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                          {payoutConfig?.zarinpalEnabled
                            ? `سیکل پایای روز بعد؛ ساعت مجاز ${payoutConfig.hours}${payoutConfig.withinHours ? '' : ' (الان خارج از ساعت مجاز است؛ در صف می‌ماند)'}؛ حداقل ${formatPrice(payoutConfig.minPayoutToman)} تومان. شبای فروشنده باید در زرین‌پال تأیید شده باشد؛ برداشت پس از تأیید واریز، خودکار «تکمیل» می‌شود.`
                            : 'غیرفعال — ZP_ACCESS_TOKEN / ZP_TERMINAL_ID روی سرور تنظیم نشده است.'}
                        </div>
                      </span>
                    </label>
                    <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
                      <input type="radio" name="pay-mode" checked={payMode === 'manual'} disabled={actionLoading || selectedWithdrawal?.status === WithdrawalStatus.APPROVED} onChange={() => setPayMode('manual')} />
                      <span>
                        <strong>واریز دستی (همراه‌بانک)</strong>
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{selectedWithdrawal?.status === WithdrawalStatus.APPROVED ? 'برای واریز دستی ابتدا «شروع پردازش» بزنید و بعد از واریز، برداشت را تکمیل کنید.' : 'خودتان واریز کرده‌اید و اکنون برداشت را «تکمیل» می‌کنید.'}</div>
                      </span>
                    </label>
                    {selectedWithdrawal?.payout?.provider === 'zarinpal' && (
                      <div style={{ marginTop: 8, fontSize: '0.75rem', color: selectedWithdrawal.payout.status === 'failed' ? '#dc2626' : '#64748b' }}>
                        وضعیت واریز زرین‌پال: {PAYOUT_STATUS_LABELS[selectedWithdrawal.payout.status] || selectedWithdrawal.payout.status}
                        {selectedWithdrawal.payout.payoutId ? ` — شناسه ${selectedWithdrawal.payout.payoutId}` : ''}
                        {selectedWithdrawal.payout.error ? ` — ${selectedWithdrawal.payout.error}` : ''}
                      </div>
                    )}
                  </div>
                  <div className="edit-field full" hidden={payMode === 'zarinpal'}>
                    <label>کد رهگیری تراکنش (اختیاری)</label>
                    <input
                      type="text"
                      value={paymentReference}
                      onChange={(e) => setPaymentReference(e.target.value)}
                      placeholder="مثال: تراکنش 123456789"
                      disabled={actionLoading}
                    />
                    <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
                      در صورت خالی گذاشتن، مرجع پرداختی که در مرحله شروع پردازش وارد شد، حفظ خواهد شد.
                    </small>
                  </div>
                  <div className="edit-field full">
                    <label>یادداشت ادمین (اختیاری)</label>
                    <textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      placeholder="یادداشت‌های مربوط به تکمیل برداشت..."
                      rows={3}
                      disabled={actionLoading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={() => setCompleteOpen(false)} disabled={actionLoading}>
            انصراف
          </button>
          <button className="edit-btn submit" onClick={handleComplete} disabled={actionLoading}>
            {actionLoading && <CircularProgress size={16} style={{ color: '#fff' }} />}
            {payMode === 'zarinpal' ? 'ثبت واریز خودکار در زرین‌پال' : 'تکمیل برداشت (واریز دستی)'}
          </button>
        </div>
      </Dialog>

      {/* Mark Failed Dialog */}
      <Dialog
        open={markFailedOpen}
        onClose={() => !actionLoading && setMarkFailedOpen(false)}
        maxWidth="sm"
        fullWidth
        className="withdrawal-action-dialog"
      >
        <div className="edit-dialog-title">
          <span>علامت‌گذاری برداشت به عنوان ناموفق</span>
          <button className="details-close" onClick={() => !actionLoading && setMarkFailedOpen(false)}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          <div className="edit-form">
            <div className="edit-card">
              <div className="edit-card-header">علامت‌گذاری ناموفق</div>
              <div className="edit-card-body">
                <div className="edit-form-grid">
                  <div className="edit-field full">
                    <label>دلیل علامت‌گذاری <span style={{ color: '#dc2626' }}>*</span></label>
                    <textarea
                      value={failureReason}
                      onChange={(e) => setFailureReason(e.target.value)}
                      placeholder="مثال: عدم تطابق اطلاعات بانکی، خطا در واریز، ..."
                      rows={2}
                      disabled={actionLoading}
                    />
                  </div>
                  <div className="edit-field full">
                    <label>یادداشت ادمین (اختیاری)</label>
                    <textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      placeholder="یادداشت‌های اضافی..."
                      rows={3}
                      disabled={actionLoading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={() => setMarkFailedOpen(false)} disabled={actionLoading}>
            انصراف
          </button>
          <button
            className="edit-btn submit"
            onClick={handleMarkFailed}
            disabled={!failureReason.trim() || actionLoading}
          >
            {actionLoading && <CircularProgress size={16} style={{ color: '#fff' }} />}
            علامت‌گذاری
          </button>
        </div>
      </Dialog>
    </div>
  );
};

export default WithdrawalManagement;
